import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { catalogCourseCta, catalogCourseHref } from "@lms/backend/lib/courses/catalog-course-cta";
import { Badge, Card, Heading, PageShell, Text } from "@/components/ui";
import { buttonClasses } from "@/components/ui/button";
import { cardClasses } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { AuthRequiredLink } from "@/components/tenant-slug-guard";
import { fetchTenantCatalog } from "@/services/api/tenants";
import { useAuthStore } from "@/stores/authStore";

export function TenantCatalogPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const t = useTranslations("catalog");
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const catalogQuery = useQuery({
    queryKey: ["tenant-catalog", tenantSlug],
    queryFn: () => fetchTenantCatalog(tenantSlug),
  });

  if (catalogQuery.isLoading) {
    return (
      <PageShell data-testid="course-catalog-loading">
        <Text tone="muted">{t("loading")}</Text>
      </PageShell>
    );
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <PageShell data-testid="course-catalog-error">
        <Heading as="h1" level="page">
          {t("unavailable")}
        </Heading>
        <Text tone="muted" className="mt-2">
          {t("unavailableHint")}
        </Text>
      </PageShell>
    );
  }

  const { tenant, courses, enrolledCourseIds } = catalogQuery.data;
  const enrolled = new Set(enrolledCourseIds);

  return (
    <PageShell
      width="app"
      pad="none"
      className="flex min-h-[min(70vh,52rem)] flex-1 flex-col py-8 sm:py-12"
      data-testid="course-catalog"
    >
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Heading as="p" level="eyebrow">
            {tenant.name}
          </Heading>
          <Heading as="h1" level="page" className="mt-1">
            {t("title")}
          </Heading>
          <Text tone="muted" size="base" className="mt-2 max-w-xl">
            {t("subtitle", { tenant: tenant.name })}
          </Text>
        </div>
        <AuthRequiredLink
          tenantSlug={tenantSlug}
          signedIn={isAuthenticated}
          className={buttonClasses({ variant: "accent", pill: true })}
          data-testid={isAuthenticated ? "catalog-dashboard-link" : "catalog-login-link"}
        >
          {isAuthenticated ? t("goToDashboard") : t("signInToEnroll")}
        </AuthRequiredLink>
      </div>

      {courses.length === 0 ? (
        <Card tone="dashed" pad="lg" className="text-center">
          <Text tone="muted">{t("empty")}</Text>
        </Card>
      ) : (
        <ul className="grid flex-1 content-start gap-5 sm:grid-cols-2">
          {courses.map((course) => {
            const cta = catalogCourseCta(enrolled.has(course.id));
            const href = catalogCourseHref(tenantSlug, course.slug, cta);
            return (
              <li key={course.id}>
                <Link
                  to={href}
                  className={cn(
                    cardClasses({ tone: "brand", pad: "none", interactive: true }),
                    "block h-full overflow-hidden",
                  )}
                  data-testid={`catalog-course-${course.slug}`}
                  data-catalog-cta={cta}
                >
                  {course.thumbnailUrl ? (
                    <img
                      src={course.thumbnailUrl}
                      alt=""
                      className="aspect-video w-full object-cover"
                    />
                  ) : null}
                  <div className="p-6">
                    <Heading as="h2" level="card" className="font-serif text-xl font-medium">
                      {course.title}
                    </Heading>
                    {course.description && (
                      <Text tone="muted" className="mt-3 line-clamp-4 leading-relaxed">
                        {course.description}
                      </Text>
                    )}
                    <div className="mt-5 flex items-center justify-between gap-3">
                      <Badge tone="accent">{course.courseType}</Badge>
                      <Text
                        as="span"
                        className="font-semibold text-[var(--text-heading)]"
                        data-testid={
                          cta === "continue"
                            ? `catalog-continue-${course.slug}`
                            : `catalog-view-${course.slug}`
                        }
                      >
                        {cta === "continue" ? t("continueLearning") : t("viewCourse")}
                      </Text>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </PageShell>
  );
}
